import { computed } from 'vue';
import type { Signee } from '@/types/Signee';
import type { Placeholder } from '@/composables/placement/placementTypes.ts';
import { PlaceholderType } from '@/types/enums/PlaceholderType.ts';
import {
  injectStrict,
  PlaceHolderComposableKey
} from '@/composables/placement/placementSymbols.ts';
import { useCreateStore } from '@/stores/create.ts';
import { useGlobalStore } from '@/stores/global.ts';
import {
  findGroupSigningForSlot,
  findSigneeGroupForSignee,
  getDefaultSigneeFileId,
  getSigneeGroupSlotIndex,
  isPlaceholderForSignee,
  isSigneeGroupQuorumSet,
  resolveSigneeSignings
} from '@/utils/signeeSignings.ts';

/**
 * Per-file signature placement state for a signee, scoped to the file currently
 * open in the viewer.
 *
 * - `placedCount` counts the signee's placements on the current file.
 *   `signeesOrdered` participants don't always carry `signings`, so it resolves
 *   the authoritative per-file data from the document's signee groups (mirrors
 *   SidebarSignatures).
 * - `navigablePlaceholders` are the rendered placeholders (already scaled to
 *   on-screen coordinates) for the signee on the current file, ordered by slot,
 *   ready to scroll the viewer to.
 *
 * Pass a getter so the state stays reactive to the source signee changing.
 */
export function useSigneeFilePlacements(getSignee: () => Signee) {
  const createStore = useCreateStore();
  const globalStore = useGlobalStore();
  const { currentPlaceholders } = injectStrict(PlaceHolderComposableKey);

  // A signee placed on other files but not this one reads as unplaced here.
  const currentFileId = computed(
    () =>
      globalStore.selectedDocumentFileId ??
      getDefaultSigneeFileId(createStore.document)
  );

  const placedCount = computed(() => {
    const signee = getSignee();

    // Quorum members share group-owned signature slots: the placement lives
    // in `signeeGroup.signings` at the signee's fixed group-wide slot index
    // (see `getSigneeGroupSlotIndex`), not in the signee's own `signings`.
    const signeeGroup = findSigneeGroupForSignee(
      signee.signeeId,
      createStore.document?.signeeGroups
    );
    if (isSigneeGroupQuorumSet(signeeGroup)) {
      const slot = getSigneeGroupSlotIndex(signeeGroup!, signee.signeeId);
      // Mirrors usePlacement: members beyond the quorum own no shared slot, so
      // they render no placeholder at all and can never be placed. Without
      // this guard they'd fall through to the legacy field below and report a
      // placement that has no placeholder behind it.
      if (slot >= (signeeGroup!.requiredSignatures ?? 0)) return 0;
      if (!currentFileId.value) return 0;
      const groupSigning = findGroupSigningForSlot(
        signeeGroup!,
        currentFileId.value,
        slot
      );
      return groupSigning?.autographPositions?.length ?? 0;
    }

    const signings = resolveSigneeSignings(signee, createStore.document);
    const signing = signings.find(
      (entry) => entry.fileId === currentFileId.value
    );
    if (signing) {
      return signing.autographPositions?.length ?? 0;
    }
    // Legacy single-file documents expose one document-wide autographPosition
    // bound to the default file. It is only a valid source in single-file mode:
    // in an envelope it is a document-wide leftover that a drop on ANY file
    // populates, so honouring it here reported file #1 as placed for a signee
    // whose only real placement lives on another file (usePlacement refuses it
    // for the same reason). `currentFileId` may be null before the files load,
    // which would otherwise make the comparison below trivially true.
    if (
      signee.autographPosition &&
      !createStore.isEnvelope &&
      currentFileId.value !== null &&
      currentFileId.value === getDefaultSigneeFileId(createStore.document)
    ) {
      return 1;
    }
    return 0;
  });

  const navigablePlaceholders = computed<Placeholder[]>(() => {
    const signee = getSignee();
    return (currentPlaceholders.value as Placeholder[])
      .filter(
        (placeholder) =>
          isPlaceholderForSignee(
            placeholder,
            signee.signeeId,
            createStore.document?.signeeGroups
          ) &&
          placeholder.fileId === currentFileId.value &&
          placeholder.placeHolderType === PlaceholderType.SIGNATURE &&
          placeholder.isPlaced
      )
      .sort((a, b) => (a.signatureSlot ?? 0) - (b.signatureSlot ?? 0));
  });

  return { currentFileId, placedCount, navigablePlaceholders };
}
